import React, { useContext } from "react";
import {
    ConfirmationPrompt,
    EllipsisDropdownPortal,
    Notification,
    STATUS_TYPES,
    Table,
    USER_PERMISSIONS,
} from "dyl-components";
import Contact from "shared/Contact";
import { Link, useParams } from "react-router-dom";
import { Checkbox, Dimmer, DropdownItem, Icon, Loader, Popup, Ref } from "semantic-ui-react";
import { BulkActionsContext } from "shared/context/BulkActionsProvider";
import { PhoneUtil, StringUtils } from "utils";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import campaignContactsActions from "actions/campaign_contacts";
import campaignsActions from "actions/campaigns";
import campaignActions from 'actions/campaigns';
import { useState } from "react";
import ModuleLink from "shared/ModuleLink";
import RestrictedView from "shared/RestrictedView";

const DialerColumns = ({ call_result, suspend_call }) => {
    return (
        <>
            <Table.Cell textAlign="center" className="CampaignContactsTableCell CampaignContactsTableCell--start">
                {PhoneUtil.SYSTEM_RESULT_OPTIONS.find(option => option.value === call_result)?.text}
            </Table.Cell>
            <Table.Cell textAlign="center" className="CampaignContactsTableCell CampaignContactsTableCell--end">
                {suspend_call && <Icon className="fas fa-ban" color='red' size='big' />}
            </Table.Cell>
        </>
    );
}

const TextColumns = ({ hasCall, text_result, opt_out }) => {
    const hasTextResult = text_result !== "none";
    return (
        <>
            <Table.Cell textAlign="center" className={!hasCall ? `CampaignContactsTableCell CampaignContactsTableCell--start ${text_result==="failed"?  "CampaignContactsTableCell--TextFailed" : ""}` : ""}>
                {hasTextResult && StringUtils.capitalize(text_result)}
            </Table.Cell>
            <Table.Cell textAlign="center">
                {hasTextResult && <Icon className="fas fa-check" size='big' />}
            </Table.Cell>
            <Table.Cell textAlign="center" className="CampaignContactsTableCell CampaignContactsTableCell--end">
                {opt_out && <Icon className="fas fa-ban" color='red' />}
            </Table.Cell>
        </>
    );
}   

export const ContactsRow = ({ contact, isCampaignDeleted, readContacts }) => {
    const [selectedContacts, setSelectedContacts] =
        useContext(BulkActionsContext);

    const isSelected = (contact_id) => {
        return selectedContacts.includes(contact_id);
    };

    const onToggleContact = (contact_id) => {
        const newSelectedContacts = isSelected(contact_id)
            ? selectedContacts.filter((id) => contact_id !== id)
            : [...selectedContacts, contact_id];
        setSelectedContacts(newSelectedContacts);
    };

    const contact_status = contact.active ? "active" : "inactive";
    const ref = useRef();

    const dispatch = useDispatch();
    const { id } = useParams();

    const onUpdateContactStatus = async () => {
        const newStatus = contact.active ? "inactive" : "active";
        try {
            await dispatch(
                campaignContactsActions.updateContactStatus(
                    id,
                    null,
                    { active: !contact.active },
                    contact.id
                )
            );
            Notification.alert(
                `Successfully set contact to ${newStatus}!`,
                STATUS_TYPES.SUCCESS
            );
            readContacts();
        } catch (e) {
            Notification.alert(
                `Failed to set contact to ${newStatus}`,
                STATUS_TYPES.ERROR
            );
        }
    };

    const isUpdatingStatus = useSelector(
        (state) => state.campaign_contacts.contactBeingUpdated === contact.id
    );

    const { campaign } = useSelector(state => ({
        campaign: state.campaigns.campaign
    }));

    const outreachTypes = (campaign?.is_company_campaign?.campaign_type || [])?.slice(0).sort();
    const isCompanyCampaign = campaign?.is_company_campaign?.start_time;

    const onRemoveContact = async () => {
        try {
            await dispatch(
                campaignsActions.campaignContactsBulkAction(
                    id,
                    { contact_id: [contact.id], action: "remove_from_campaign" }
                )
            );
            setIsRemoving(false);
            Notification.alert(
                `Successfully removed contact from campaign!`,
                STATUS_TYPES.SUCCESS
            );
            dispatch(campaignActions.readCampaign(Number(id)));
            readContacts();
        } catch (e) {
            Notification.alert(
                `Failed to remove contact from campaign`,
                STATUS_TYPES.ERROR
            );
        }
    };

    const [isRemoving, setIsRemoving] = useState(false);

    const isApplyingBulkAction = useSelector(state => state.campaigns.isApplyingBulkAction);

    return (
        <Table.Row className="CampaignContactsTableRow">
            <Table.Cell>
                <Checkbox
                    checked={isSelected(contact.id)}
                    onChange={() => {
                        onToggleContact(contact.id);
                    }}
                />
            </Table.Cell>
            <Table.Cell>
                <Contact
                    contact={contact}
                    name={`${contact.first_name} ${contact.last_name}`}
                    linkToContact={
                        <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[contact?.assignees?.owner_id || 0, contact?.assignees?.coowner_id || 0]} >
                            <Link
                                to={`/contact/${contact.id}`}
                            >{`${contact.first_name} ${contact.last_name}`}</Link>
                        </RestrictedView>
                    }
                    hideId
                />
            </Table.Cell>

            <Table.Cell>
                {contact?.campaign_converted ? (
                    <>
                        <div>
                            Yes{campaign.campaign_conversion === "custom" ? "" : `, ${StringUtils.capitalize(campaign.campaign_conversion)}`}
                        </div>
                        {contact.pipeline_id && <div>
                            <ModuleLink pipeline_id={contact.pipeline_id} includeLabel unlinked={contact.unlinked} record={contact}/>
                        </div>}
                        {campaign.campaign_conversion === "customer" && (
                            <>ID: {contact.unlinked ? "Unlinked" 
                                : <RestrictedView type="link" userPermission={USER_PERMISSIONS.ACCOUNT_VIEW} userRestrict assignmentIds={[contact?.account?.assignees?.owner_id || 0, contact?.account?.assignees?.coowner_id || 0]}><Link to={`/account/${contact?.account?.account_id}`}>{contact?.account?.account_id}</Link></RestrictedView>
                            }</>
                        )}
                    </>
                ) : "No"}
            </Table.Cell>
            {!isCompanyCampaign ? (
                <DialerColumns call_result={contact?.call_result} suspend_call={contact?.suspend_call} />
            ) : (
                outreachTypes.map(type => {
                    if (type === "dialer") {
                        return <DialerColumns call_result={contact?.call_result} suspend_call={contact?.suspend_call} />
                    }
                    if (type === "text") {
                        return <TextColumns hasCall={outreachTypes.includes("dialer")} opt_out={contact?.text_optout} text_result={contact?.text_result} />
                    }
                    return null;
                }).filter(Boolean)
            )}
            <Table.Cell className={`ContactStatus__${contact_status}`}>
                <Icon
                    name="circle"
                    className={`ContactStatus__${contact_status}`}
                />
                {StringUtils.capitalize(contact_status)}
            </Table.Cell>
            <Table.Cell>
                {!isCampaignDeleted && (
                    <Ref innerRef={ref}>
                        <EllipsisDropdownPortal reference={ref}>
                            <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_ADD}>
                                <DropdownItem
                                    text="Remove"
                                    disabled={isUpdatingStatus}
                                    onClick={() => {
                                        setIsRemoving(true);
                                    }}
                                />
                            </RestrictedView>
                            <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_ADD}>
                                <DropdownItem
                                    text={
                                        <span>
                                            Set to{" "}
                                            {contact.active ? "Inactive" : "Active"}{" "}
                                            {isUpdatingStatus && (
                                                <Icon
                                                    className="fas fa-circle-notch"
                                                    loading
                                                />
                                            )}
                                        </span>
                                    }
                                    onClick={onUpdateContactStatus}
                                    disabled={isUpdatingStatus}
                                />
                            </RestrictedView>
                        </EllipsisDropdownPortal>
                    </Ref>
                )}
                <Popup
                    open={isRemoving}
                    position="bottom center"
                    context={ref}
                    onClose={() => {
                        setIsRemoving(false);
                    }}
                    content={
                        <div>
                            <Dimmer active={isApplyingBulkAction} />
                            <Loader active={isApplyingBulkAction} />
                            <ConfirmationPrompt
                                onConfirm={onRemoveContact}
                                onCancel={() => {
                                    setIsRemoving(false);
                                }}
                            />
                        </div>
                    }
                    hoverable
                />
            </Table.Cell>
        </Table.Row>
    );
};
